import { MatterRelatedMatterContextMenu } from "./MatterRelatedMatterContextMenu";
import { InsuranceCopyModeModal } from "@/components/features/matter/insurance/InsuranceCopyModeModal";
import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { MatterSubItemFileReferencesContextMenu } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesContextMenu";
import { MatterSubItemFileReferencesModal } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesModal";
import { FileReferences } from "@/components/features/matter/file-repo/FileReferences";
import {
  MatterSubItemFileReferenceUpload,
  type MatterSubItemFileReferenceUploadHandle,
} from "@/components/features/matter/file-repo/MatterSubItemFileReferenceUpload";
import { MatterSubItemNotesContextMenu } from "@/components/features/matter/notes/MatterSubItemNotesContextMenu";
import { MatterSubItemNotesModal } from "@/components/features/matter/notes/MatterSubItemNotesModal";
import { MatterSubItemTasksContextMenu } from "@/components/features/matter/tasks/MatterSubItemTasksContextMenu";
import { MatterSubItemTasksModal } from "@/components/features/matter/tasks/MatterSubItemTasksModal";
import { MatterSubItemGenerateDocumentContextMenu } from "@/components/features/matter/context-menus/MatterSubItemGenerateDocumentContextMenu";
import { MatterSubItemCreateSignatureRequestContextMenu } from "@/components/features/matter/context-menus/MatterSubItemCreateSignatureRequestContextMenu";
import { EditContactModal } from "@/components/features/contact/AddOrEditContactModal/EditContactModal";
import SelectContactModal from "@/components/features/contact/SelectContactModal/SelectContactModal";
import {
  ActionConfirmationModal,
  ContactSearchableContextMenuSubmenu,
  ContextMenu,
  ContextMenuSeparator,
  SearchableContextMenuSubmenu,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  useCreateConnection,
  useDeleteConnection,
  useReplaceConnection,
} from "@/lib/data/react-query/connections/connection";
import {
  useDeleteMatterInsurance,
  useUpdateMatterInsurance,
} from "@/lib/data/react-query/matters/matter-insurance";
import {
  useCopyPartyInsuranceToRelatedMatters,
  useUnlinkObjectFromMatter,
} from "@/lib/data/react-query/matters/matter-related-object";
import { MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS } from "@/lib/data/static/matters/insuranceCoverageStatics";
import {
  CATEGORIES_BY_INSURANCE_TYPE,
  isInsuranceType,
} from "@/lib/data/static/matters/insuranceStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getRelatedMatterSelectionOptions } from "@/lib/utils/relatedMatters";
import type {
  Contact,
  Connection,
  Matter,
  MatterContactConnection,
  MatterInsurance,
} from "@/lib/types";
import type { MatterPartySlideoverRole } from "@/lib/utils/matterPartyCoverage";
import {
  createInsuranceContactConnectionInput,
  getInsuranceContactConnection,
  getInsuranceContactConnections,
  type SupportedInsuranceContactRole,
} from "@/lib/utils/connections/insurance-contact-connections";
import {
  BadgeCheck,
  Building2,
  Tag,
  UserRound,
  UserRoundCheck,
  UserRoundCog,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import toast from "react-hot-toast";

interface MatterInsuranceContextMenuProps {
  insurance: MatterInsurance;
  insuranceLabel: string;
  matter: Matter;
  onEdit: () => void;
  partyConnection: MatterContactConnection;
  partyRole: MatterPartySlideoverRole;
  trigger: ReactNode;
}

type InsuranceContactSelection = {
  connection?: Connection;
  role: SupportedInsuranceContactRole;
};

export const MatterInsuranceContextMenu = ({
  insurance,
  insuranceLabel,
  matter,
  onEdit,
  partyConnection,
  partyRole,
  trigger,
}: MatterInsuranceContextMenuProps) => {
  const { hasActionPermission, currentUser } = useUser();
  const { openCommunicationModal, openSendMatterEmailModal } = useGlobalModal();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isUnlinkConfirmationOpen, setIsUnlinkConfirmationOpen] =
    useState(false);
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const fileReferenceUploadRef =
    useRef<MatterSubItemFileReferenceUploadHandle>(null);
  const [isFileReferencesOpen, setIsFileReferencesOpen] = useState(false);
  const [isCreateFileReferenceOpen, setIsCreateFileReferenceOpen] =
    useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isTasksOpen, setIsTasksOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [coverageSearch, setCoverageSearch] = useState("");
  const [contactSelection, setContactSelection] =
    useState<InsuranceContactSelection>();
  const [contactBeingEdited, setContactBeingEdited] = useState<Contact>();
  const [pendingClientInsuranceMatterIds, setPendingClientInsuranceMatterIds] =
    useState<string[]>([]);
  const deleteInsurance = useDeleteMatterInsurance();
  const unlinkInsurance = useUnlinkObjectFromMatter({
    objectLabel: "Insurance",
  });
  const copyPartyInsurance = useCopyPartyInsuranceToRelatedMatters();
  const updateMatterInsurance = useUpdateMatterInsurance({ showToast: false });
  const createConnection = useCreateConnection();
  const replaceConnection = useReplaceConnection();
  const deleteConnection = useDeleteConnection();
  const canUpdateInsurance = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterInsurance,
    ACTION_PERMISSION_VALUES.update,
  );
  const canUpdateContact = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const canSendMatterEmail = Boolean(true);
  const canTextContact = Boolean(true);
  const canDeleteInsurance = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterInsurance,
    ACTION_PERMISSION_VALUES.delete,
  );
  const copyValue = [
    insuranceLabel,
    insurance.type ? `Type: ${insurance.type}` : undefined,
    insurance.category ? `Category: ${insurance.category}` : undefined,
    insurance.policyNumber ? `Policy #: ${insurance.policyNumber}` : undefined,
    insurance.claimNumber ? `Claim #: ${insurance.claimNumber}` : undefined,
  ]
    .filter(Boolean)
    .join("\n");
  const subItem = { id: insurance.id, type: "insurance" } as const;
  const getInsuranceConnection = (role: SupportedInsuranceContactRole) =>
    getInsuranceContactConnection(insurance, role);
  const insuredConnections = getInsuranceContactConnections(
    insurance,
    "Insured",
  );
  const policyHolder = getInsuranceContactConnection(
    insurance,
    "Policy Holder",
  );
  const coverageEnabled = insurance.coverageEnabled !== false;
  const insuranceType = isInsuranceType(insurance.type)
    ? insurance.type
    : undefined;
  const isAutoInsurance = insuranceType === "Auto";
  const categoryOptions = insuranceType
    ? CATEGORIES_BY_INSURANCE_TYPE[insuranceType]
    : [];
  const showCategory =
    coverageEnabled &&
    insuranceType !== undefined &&
    insuranceType !== "Auto" &&
    insuranceType !== "Excess";
  const showCoverageStatus =
    coverageEnabled &&
    insuranceType !== undefined &&
    insuranceType !== "Health";
  const visibleCategories = categoryOptions.filter((category) =>
    category.toLowerCase().includes(categorySearch.trim().toLowerCase()),
  );
  const visibleCoverageStatuses =
    MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS.filter((coverageStatus) =>
      coverageStatus
        .toLowerCase()
        .includes(coverageSearch.trim().toLowerCase()),
    );
  const hasPartyAsInsured = insuredConnections.some(
    (connection) => connection.targetObjectId === partyConnection.contactId,
  );
  const hasPartyAsPolicyHolder =
    policyHolder?.targetObjectId === partyConnection.contactId;
  const showPartyInsuranceRoleError = () => {
    toast.error(
      `${partyRole} must be selected as either Insured or Policy Holder.`,
    );
  };
  const updateInsuranceAttribute = async (
    attributes: Partial<MatterInsurance>,
    successMessage: string,
  ) => {
    await updateMatterInsurance.mutateAsync({
      id: insurance.id,
      matterId: matter.id,
      dateModified: insurance.dateModified,
      ...attributes,
    });
    toast.success(successMessage);
  };
  const setInsuranceContact = async (
    contact: Contact,
    role: SupportedInsuranceContactRole,
    existingConnection?: Connection,
  ) => {
    if (contact.id === existingConnection?.targetObjectId) return;
    if (
      coverageEnabled &&
      (role === "Insured" || role === "Policy Holder") &&
      contact.id !== partyConnection.contactId &&
      !(hasPartyAsInsured || (role === "Insured" && hasPartyAsPolicyHolder))
    ) {
      showPartyInsuranceRoleError();
      return;
    }

    if (existingConnection) {
      await replaceConnection.mutateAsync({
        connectionId: existingConnection.id,
        matterId: matter.id,
        dateModified: existingConnection.dateModified,
        operation: "replace",
        replacement: createInsuranceContactConnectionInput(
          insurance.id,
          contact.id,
          role,
        ),
      });
    } else {
      await createConnection.mutateAsync({
        matterId: matter.id,
        ...createInsuranceContactConnectionInput(
          insurance.id,
          contact.id,
          role,
        ),
      });
    }
    toast.success(
      `${role === "Insured" && !coverageEnabled ? "Uninsured" : role} updated successfully`,
    );
  };
  const deleteInsuranceContact = async (
    contact: Contact,
    connection?: Connection,
  ) => {
    if (connection?.targetObjectId !== contact.id) return;

    await deleteConnection.mutateAsync({
      connectionId: connection.id,
      matterId: matter.id,
    });
    toast.success("Contact removed successfully");
  };
  const emailContact = (contact: Contact) => {
    const email =
      contact.emails.find(
        (contactEmail) =>
          contactEmail.isPrimary && Boolean(contactEmail.address?.trim()),
      )?.address ??
      contact.emails.find((contactEmail) =>
        Boolean(contactEmail.address?.trim()),
      )?.address;
    if (!email) return;

    openSendMatterEmailModal({
      matterId: matter.id,
      contactId: contact.id,
      toEmails: [email],
      templateData: { firm_name: currentUser.firm.name },
      title: "Send email",
      description: "Compose and send an email for this matter.",
    });
  };
  const textContact = (contact: Contact) => {
    openCommunicationModal({ contactId: contact.id, matterId: matter.id });
  };
  const insuranceContactRoles = coverageEnabled
    ? [
        {
          connection: getInsuranceConnection("Insurer"),
          icon: Building2,
          label: "Insurer" as const,
        },
        {
          connection: insuredConnections[0],
          connections: insuredConnections,
          icon: UserRoundCheck,
          label: "Insured" as const,
        },
        {
          connection: policyHolder,
          icon: UserRound,
          label: "Policy Holder" as const,
        },
        ...(isAutoInsurance
          ? [
              {
                connection: getInsuranceConnection("Bodily Injury Adjuster"),
                icon: UserRoundCog,
                label: "Bodily Injury Adjuster" as const,
              },
              {
                connection: getInsuranceConnection("Property Damage Adjuster"),
                icon: UserRoundCog,
                label: "Property Damage Adjuster" as const,
              },
              ...(insurance.medPay
                ? [
                    {
                      connection: getInsuranceConnection("MedPay Adjuster"),
                      icon: UserRoundCog,
                      label: "MedPay Adjuster" as const,
                    },
                  ]
                : []),
              {
                connection: getInsuranceConnection("Adjuster Supervisor"),
                icon: UserRoundCog,
                label: "Adjuster Supervisor" as const,
              },
            ]
          : []),
        ...(insurance.subrogation
          ? [
              {
                connection: getInsuranceConnection("Subrogation Vendor"),
                icon: Building2,
                label: "Subrogation Vendor" as const,
              },
              {
                connection: getInsuranceConnection(
                  "Subrogation Representative",
                ),
                icon: UserRound,
                label: "Subrogation Representative" as const,
              },
            ]
          : []),
      ]
    : [
        {
          connection: insuredConnections[0],
          connections: insuredConnections,
          icon: UserRoundCheck,
          label: "Insured" as const,
        },
      ];

  return (
    <>
      <ContextMenu
        entityName="insurance"
        open={{ onSelect: onEdit }}
        copy={{ value: copyValue, successLabel: "Insurance details" }}
        delete={
          canDeleteInsurance
            ? {
                disabled: deleteInsurance.isPending,
                onSelect: () => {
                  setIsDeleteConfirmationOpen(true);
                },
              }
            : false
        }
        unlink={
          canDeleteInsurance
            ? {
                disabled: unlinkInsurance.isPending,
                label: "Unlink insurance",
                onSelect: () => {
                  setIsUnlinkConfirmationOpen(true);
                },
              }
            : false
        }
        trigger={trigger}
      >
        {canUpdateInsurance
          ? insuranceContactRoles.map(
              ({ connection, connections, icon: Icon, label }) => (
                <ContactSearchableContextMenuSubmenu
                  matterId={matter.id}
                  addLabel={`Add a ${label}`}
                  key={label}
                  isRequired={
                    label === "Policy Holder" &&
                    coverageEnabled &&
                    hasPartyAsPolicyHolder &&
                    !hasPartyAsInsured
                  }
                  isContactRequired={(contact) =>
                    label === "Insured" &&
                    (insuredConnections.length === 1 ||
                      (contact.id === partyConnection.contactId &&
                        !hasPartyAsPolicyHolder))
                  }
                  selectedContactIds={
                    connections?.map(
                      (selectedConnection) => selectedConnection.targetObjectId,
                    ) ?? (connection ? [connection.targetObjectId] : [])
                  }
                  selectionMode={label === "Insured" ? "multiple" : "single"}
                  title={
                    <>
                      <Icon />
                      {label === "Insured" && !coverageEnabled
                        ? "Uninsured"
                        : label}
                    </>
                  }
                  onAddContact={() => {
                    setContactSelection({ role: label });
                  }}
                  onChangeContact={() => {
                    setContactSelection({ connection, role: label });
                  }}
                  onDeleteContact={(contact) => {
                    void deleteInsuranceContact(
                      contact,
                      connections?.find(
                        (selectedConnection) =>
                          selectedConnection.targetObjectId === contact.id,
                      ) ?? connection,
                    );
                  }}
                  onEditContact={
                    canUpdateContact ? setContactBeingEdited : undefined
                  }
                  onEmailContact={canSendMatterEmail ? emailContact : undefined}
                  onTextContact={canTextContact ? textContact : undefined}
                />
              ),
            )
          : null}
        {canUpdateInsurance ? <ContextMenuSeparator /> : null}
        {canUpdateInsurance && showCategory ? (
          <SearchableContextMenuSubmenu
            emptyMessage="No categories found."
            getItemKey={(category) => category}
            items={visibleCategories}
            onOpenChange={(open) => {
              if (!open) {
                setCategorySearch("");
              }
            }}
            onSearchChange={setCategorySearch}
            onSelectItem={(category) => {
              void updateInsuranceAttribute(
                { category },
                "Insurance category updated successfully",
              );
            }}
            placeholder="Search categories..."
            renderItem={(category) => <>{category}</>}
            search={categorySearch}
            selectedItemKeys={insurance.category ? [insurance.category] : []}
            title={
              <>
                <Tag />
                Category
              </>
            }
          />
        ) : null}
        {canUpdateInsurance && showCoverageStatus ? (
          <SearchableContextMenuSubmenu
            emptyMessage="No coverage statuses found."
            getItemKey={(coverageStatus) => coverageStatus}
            items={visibleCoverageStatuses}
            onOpenChange={(open) => {
              if (!open) {
                setCoverageSearch("");
              }
            }}
            onSearchChange={setCoverageSearch}
            onSelectItem={(coverage) => {
              void updateInsuranceAttribute(
                { coverage },
                "Insurance coverage status updated successfully",
              );
            }}
            placeholder="Search coverage statuses..."
            renderItem={(coverageStatus) => <>{coverageStatus}</>}
            search={coverageSearch}
            selectedItemKeys={insurance.coverage ? [insurance.coverage] : []}
            title={
              <>
                <BadgeCheck />
                Coverage status
              </>
            }
          />
        ) : null}
        {canUpdateInsurance && (showCategory || showCoverageStatus) ? (
          <ContextMenuSeparator />
        ) : null}
        <MatterSubItemGenerateDocumentContextMenu
          matter={matter}
          subItem={subItem}
        />
        <MatterSubItemCreateSignatureRequestContextMenu
          matter={matter}
          subItem={subItem}
        />
        <MatterRelatedMatterContextMenu
          matter={matter}
          objectId={insurance.id}
          objectType="insurance"
          insuranceParty={{
            connectionId: partyConnection.id,
            role: partyRole,
          }}
          onClientInsuranceCopyRequested={setPendingClientInsuranceMatterIds}
        />
        <MatterSubItemTasksContextMenu
          matter={matter}
          onOpenTasks={() => {
            setIsTasksOpen(true);
          }}
          subItem={subItem}
        />
        <MatterSubItemNotesContextMenu
          matter={matter}
          onOpenNotes={() => {
            setIsNotesOpen(true);
          }}
          subItem={subItem}
        />
        <MatterSubItemFileReferencesContextMenu
          onOpenFileReferences={() => {
            setIsFileReferencesOpen(true);
          }}
          onCreateFileReferences={() => {
            setIsCreateFileReferenceOpen(true);
          }}
          onUploadFileReference={() => {
            fileReferenceUploadRef.current?.openFilePicker();
          }}
        />
        <MatterSubItemEventLogsContextMenu
          onOpenEventLogs={() => {
            setIsEventLogsOpen(true);
          }}
        />
      </ContextMenu>
      <InsuranceCopyModeModal
        destinations={getRelatedMatterSelectionOptions(matter).filter(
          ({ id }) => pendingClientInsuranceMatterIds.includes(id),
        )}
        open={pendingClientInsuranceMatterIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setPendingClientInsuranceMatterIds([]);
        }}
        loading={copyPartyInsurance.isPending}
        onSubmit={(placements) => {
          copyPartyInsurance.mutate({
            insuranceId: insurance.id,
            sourceMatterContactConnectionId: partyConnection.id,
            sourceMatterId: matter.id,
            placements,
          });
          setPendingClientInsuranceMatterIds([]);
        }}
      />
      <ActionConfirmationModal
        open={isDeleteConfirmationOpen}
        setOpen={(open) => {
          if (!open && !deleteInsurance.isPending) {
            setIsDeleteConfirmationOpen(false);
          }
        }}
        title={`Are you sure you want to delete "${insuranceLabel}"?`}
        description="This permanently deletes the insurance from every matter. This action cannot be undone."
        buttonText="Delete insurance"
        afterActionFn={async () => {
          await deleteInsurance.mutateAsync({
            matterId: matter.id,
            id: insurance.id,
          });
        }}
        isActionPending={deleteInsurance.isPending}
      />
      <ActionConfirmationModal
        open={isUnlinkConfirmationOpen}
        setOpen={(open) => {
          if (!open && !unlinkInsurance.isPending) {
            setIsUnlinkConfirmationOpen(false);
          }
        }}
        title={`Unlink "${insuranceLabel}" from this matter?`}
        description="This removes the insurance from this matter only. If this is its last matter, the insurance will be deleted."
        buttonText="Unlink insurance"
        afterActionFn={async () => {
          await unlinkInsurance.mutateAsync({
            matterId: matter.id,
            objectId: insurance.id,
            objectType: "insurance",
          });
        }}
        isActionPending={unlinkInsurance.isPending}
      />
      <SelectContactModal
        matterId={matter.id}
        open={Boolean(contactSelection)}
        setOpen={(open) => {
          if (!open) setContactSelection(undefined);
        }}
        title={`Select ${contactSelection?.role ?? "contact"}`}
        defaultSearchObject={
          contactSelection?.role === "Insurer"
            ? { organizationSubTypes: ["Insurance"] }
            : undefined
        }
        defaultValues={
          contactSelection?.role === "Insurer"
            ? { type: "organization", organizationSubTypes: ["Insurance"] }
            : undefined
        }
        onSelectContact={async (contact) => {
          if (!contactSelection) return;
          await setInsuranceContact(
            contact,
            contactSelection.role,
            contactSelection.connection,
          );
        }}
      />
      <EditContactModal
        activeContact={contactBeingEdited}
        activeContactId={contactBeingEdited?.id}
        hasEditPermission={canUpdateContact}
        open={Boolean(contactBeingEdited)}
        setOpen={(open) => {
          if (!open) setContactBeingEdited(undefined);
        }}
      />
      <MatterSubItemTasksModal
        open={isTasksOpen}
        onOpenChange={setIsTasksOpen}
        entityLabel={insuranceLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemNotesModal
        open={isNotesOpen}
        onOpenChange={setIsNotesOpen}
        entityLabel={insuranceLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemFileReferencesModal
        open={isFileReferencesOpen}
        onOpenChange={(open) => {
          setIsFileReferencesOpen(open);
          if (!open) {
            setIsCreateFileReferenceOpen(false);
          }
        }}
        createFileReferenceOpen={
          isFileReferencesOpen ? isCreateFileReferenceOpen : false
        }
        onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        entityLabel={insuranceLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemEventLogsModal
        open={isEventLogsOpen}
        onOpenChange={setIsEventLogsOpen}
        entityLabel={insuranceLabel}
        matter={matter}
        subItem={subItem}
      />
      {isCreateFileReferenceOpen && !isFileReferencesOpen ? (
        <FileReferences
          matter={matter}
          subItem={subItem}
          createOnly
          createFileReferenceOpen
          onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        />
      ) : null}
      <MatterSubItemFileReferenceUpload
        ref={fileReferenceUploadRef}
        matter={matter}
        subItem={subItem}
      />
    </>
  );
};
